function ImpactMap() {
    try {
        const [loading, setLoading] = React.useState(true);
        const [error, setError] = React.useState(null);

        React.useEffect(() => {
            let cleanup = null;
            
            async function initMap() {
                try {
                    setLoading(true);
                    
                    // Wait a moment for the DOM to be ready
                    await new Promise(resolve => setTimeout(resolve, 100));
                    
                    const mapContainer = document.getElementById('impact-map');
                    if (!mapContainer) {
                        throw new Error('Map container not found');
                    }
                    
                    // Dynamically import Three.js
                    let THREE;
                    try {
                        THREE = await import('three');
                    } catch (importError) {
                        console.error('Failed to import Three.js:', importError);
                        setError('Could not load 3D library');
                        setLoading(false);
                        return;
                    }
                    
                    // Set up the scene
                    const scene = new THREE.Scene();
                    scene.background = new THREE.Color(0x091227);
                    
                    const camera = new THREE.PerspectiveCamera(
                        60,
                        mapContainer.clientWidth / mapContainer.clientHeight,
                        0.1,
                        1000
                    );
                    camera.position.set(0, 4, 10);
                    camera.lookAt(0, 0, 0);
                    
                    const renderer = new THREE.WebGLRenderer({ antialias: true });
                    renderer.setSize(mapContainer.clientWidth, mapContainer.clientHeight);
                    renderer.setPixelRatio(window.devicePixelRatio);
                    mapContainer.appendChild(renderer.domElement);
                    
                    // Add lights
                    const ambientLight = new THREE.AmbientLight(0xffffff, 0.3);
                    scene.add(ambientLight);
                    
                    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
                    directionalLight.position.set(5, 10, 5);
                    scene.add(directionalLight);
                    
                    // --- Realistic Earth Texture ---
                    const earthTextureUrl = 'https://upload.wikimedia.org/wikipedia/commons/9/97/The_Earth_seen_from_Apollo_17.jpg'; // NASA Blue Marble
                    const textureLoader = new THREE.TextureLoader();
                    const earthTexture = await new Promise((resolve, reject) => {
                        textureLoader.load(earthTextureUrl, resolve, undefined, reject);
                    });
                    
                    // Create a sphere representing Earth with texture
                    const earthGeometry = new THREE.SphereGeometry(3, 32, 32);
                    const earthMaterial = new THREE.MeshStandardMaterial({
                        map: earthTexture,
                        metalness: 0.1,
                        roughness: 0.8,
                        emissive: 0x072436,
                        emissiveIntensity: 0.2
                    });
                    const earth = new THREE.Mesh(earthGeometry, earthMaterial);
                    scene.add(earth);
                    
                    // --- Locations: Only London is a base, others are planned ---
                    const locations = [
                        { lat: 51.5074, lng: -0.1278, name: "London", type: "base" }, // England base
                        { lat: 37.7749, lng: -122.4194, name: "San Francisco", type: "planned" },
                        { lat: 40.7128, lng: -74.0060, name: "New York", type: "planned" },
                        { lat: 34.0522, lng: -118.2437, name: "Los Angeles", type: "planned" },
                        { lat: 48.8566, lng: 2.3522, name: "Paris", type: "planned" },
                        { lat: -33.8688, lng: 151.2093, name: "Sydney", type: "planned" },
                        { lat: 1.3521, lng: 103.8198, name: "Singapore", type: "planned" },
                        { lat: 35.6762, lng: 139.6503, name: "Tokyo", type: "planned" },
                        { lat: -6.2088, lng: 106.8456, name: "Jakarta", type: "planned" },
                        { lat: 28.6139, lng: 77.2090, name: "New Delhi", type: "planned" },
                        { lat: -1.2921, lng: 36.8219, name: "Nairobi", type: "planned" },
                        { lat: 30.0444, lng: 31.2357, name: "Cairo", type: "planned" },
                        { lat: -34.6037, lng: -58.3816, name: "Buenos Aires", type: "planned" },
                        { lat: -15.7942, lng: -47.8822, name: "Brasilia", type: "planned" },
                    ];
                    
                    // Convert lat/lng to 3D coordinates on a sphere
                    function latLngTo3d(lat, lng, radius) {
                        const phi = (90 - lat) * Math.PI / 180;
                        const theta = (lng + 180) * Math.PI / 180;
                        const x = -radius * Math.sin(phi) * Math.cos(theta);
                        const y = radius * Math.cos(phi);
                        const z = radius * Math.sin(phi) * Math.sin(theta);
                        return new THREE.Vector3(x, y, z);
                    }
                    
                    // Add markers for each location
                    const markers = [];
                    locations.forEach(location => {
                        try {
                            const position = latLngTo3d(location.lat, location.lng, 3);
                            const markerGeometry = new THREE.SphereGeometry(0.08, 16, 16);
                            let markerColor, markerEmissive;
                            if (location.type === 'base') {
                                markerColor = 0x10B981; // Green for base
                                markerEmissive = 0x10B981;
                            } else {
                                markerColor = 0x888888; // Gray for planned
                                markerEmissive = 0x888888;
                            }
                            const markerMaterial = new THREE.MeshStandardMaterial({ 
                                color: markerColor,
                                emissive: markerEmissive,
                                emissiveIntensity: 0.5
                            });
                            const marker = new THREE.Mesh(markerGeometry, markerMaterial);
                            marker.position.copy(position);
                            scene.add(marker);
                            // Add a pulsing effect only for the base
                            markers.push({
                                mesh: marker,
                                position: position,
                                name: location.name,
                                type: location.type,
                                baseScale: 1,
                                phase: Math.random() * Math.PI * 2
                            });
                        } catch (markerError) {
                            console.warn(`Error creating marker for ${location.name}:`, markerError);
                        }
                    });
                    
                    // Add connections between locations
                    locations.forEach((fromLocation, i) => {
                        // Connect to the next 2-3 locations to avoid too many lines
                        for (let j = 1; j <= 3; j++) {
                            if (i + j < locations.length) {
                                try {
                                    const toLocation = locations[i + j];
                                    const fromPosition = latLngTo3d(fromLocation.lat, fromLocation.lng, 3);
                                    const toPosition = latLngTo3d(toLocation.lat, toLocation.lng, 3);
                                    
                                    // Create a curve that arcs above the surface of the earth
                                    const points = [];
                                    const segments = 20;
                                    
                                    for (let k = 0; k <= segments; k++) {
                                        const t = k / segments;
                                        const pos = new THREE.Vector3().lerpVectors(fromPosition, toPosition, t);
                                        // Add height to the middle of the arc
                                        const arc = Math.sin(Math.PI * t) * 0.5;
                                        pos.normalize().multiplyScalar(3 + arc);
                                        points.push(pos);
                                    }
                                    
                                    const curve = new THREE.CatmullRomCurve3(points);
                                    const geometry = new THREE.TubeGeometry(curve, 20, 0.02, 8, false);
                                    const material = new THREE.MeshStandardMaterial({ 
                                        color: 0x60A5FA,
                                        transparent: true,
                                        opacity: 0.4,
                                        emissive: 0x60A5FA,
                                        emissiveIntensity: 0.2
                                    });
                                    
                                    const tube = new THREE.Mesh(geometry, material);
                                    scene.add(tube);
                                } catch (connectionError) {
                                    console.warn(`Error creating connection from ${fromLocation.name}:`, connectionError);
                                }
                            }
                        }
                    });
                    
                    // Add subtle cloud layer
                    const cloudGeometry = new THREE.SphereGeometry(3.05, 32, 32);
                    const cloudMaterial = new THREE.MeshStandardMaterial({
                        color: 0xffffff,
                        transparent: true,
                        opacity: 0.2,
                        alphaMap: new THREE.TextureLoader().load('https://i.imgur.com/zw5qPNO.png', 
                            undefined, 
                            () => {}, 
                            (err) => console.warn('Failed to load cloud texture:', err)
                        )
                    });
                    const clouds = new THREE.Mesh(cloudGeometry, cloudMaterial);
                    scene.add(clouds);
                    
                    // Animation state management
                    let animationId;
                    let isActive = true;
                    
                    // Handle window resize
                    function onResize() {
                        if (!mapContainer || !mapContainer.isConnected) return;
                        
                        camera.aspect = mapContainer.clientWidth / mapContainer.clientHeight;
                        camera.updateProjectionMatrix();
                        renderer.setSize(mapContainer.clientWidth, mapContainer.clientHeight);
                    }
                    
                    window.addEventListener('resize', onResize);
                    
                    // Animation loop
                    function animate() {
                        if (!isActive || !mapContainer.isConnected) {
                            // Stop animation if component is unmounted
                            cancelAnimationFrame(animationId);
                            return;
                        }
                        
                        animationId = requestAnimationFrame(animate);
                        
                        // Rotate the earth
                        earth.rotation.y += 0.001;
                        clouds.rotation.y += 0.0012;
                        
                        // Animate markers with a pulsing effect
                        const time = Date.now() * 0.001;
                        markers.forEach(marker => {
                            if (marker.type === 'base') {
                                const pulse = 1 + Math.sin(time + marker.phase) * 0.15;
                                marker.mesh.scale.set(pulse, pulse, pulse);
                            }
                        });
                        
                        renderer.render(scene, camera);
                    }
                    
                    // Start animation
                    animationId = requestAnimationFrame(animate);
                    setLoading(false);
                    
                    // Return cleanup function
                    cleanup = () => {
                        isActive = false;
                        cancelAnimationFrame(animationId);
                        
                        if (mapContainer && mapContainer.contains(renderer.domElement)) {
                            mapContainer.removeChild(renderer.domElement);
                        }
                        
                        window.removeEventListener('resize', onResize);
                        
                        // Dispose of geometries and materials
                        [earthGeometry, cloudGeometry].forEach(geo => geo.dispose());
                        [earthMaterial, cloudMaterial].forEach(mat => mat.dispose());
                        
                        // Dispose marker resources
                        markers.forEach(marker => {
                            if (marker.mesh && marker.mesh.geometry) {
                                marker.mesh.geometry.dispose();
                            }
                            if (marker.mesh && marker.mesh.material) {
                                marker.mesh.material.dispose();
                            }
                        });
                        
                        // Clear scene
                        scene.clear();
                        renderer.dispose();
                    };
                } catch (error) {
                    console.error('Error initializing map:', error);
                    setError(error.message);
                    setLoading(false);
                }
            }
            
            initMap();
            
            return () => {
                if (typeof cleanup === 'function') {
                    try {
                        cleanup();
                    } catch (error) {
                        console.warn('Error in map cleanup:', error);
                    }
                }
            };
        }, []);

        return (
            <section className="py-24 bg-gray-900">
                <div className="container mx-auto px-4">
                    <h2 className="text-4xl font-bold text-center mb-6 text-white">
                        Global <span className="text-green-400">Impact</span>
                    </h2>
                    <p className="text-xl text-gray-300 text-center mb-16 max-w-3xl mx-auto">
                        Our impact extends across the globe, connecting schools and donation centers to create a sustainable technology ecosystem.
                    </p>
                    
                    {/* 3D Globe Visualization */}
                    <div className="relative w-full h-[500px] mb-16 rounded-xl overflow-hidden">
                        <div id="impact-map" className="absolute inset-0 bg-gray-800/50 rounded-xl">
                            {loading && (
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
                                </div>
                            )}
                            {error && (
                                <div className="absolute inset-0 flex items-center justify-center text-center p-4">
                                    <div>
                                        <i className="fas fa-globe text-5xl text-gray-600 mb-4"></i>
                                        <p className="text-yellow-400 text-xl mb-2">Map Visualization Unavailable</p>
                                        <p className="text-gray-400">We're working on improving our global impact visualization</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    
                    {/* Impact Stats */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
                        <div className="bg-gray-800/50 rounded-xl p-6 backdrop-blur-sm border border-gray-700 hover:border-green-500/50 hover:shadow-[0_0_15px_rgba(16,185,129,0.15)] transition-all duration-300">
                            <div className="flex items-center mb-4">
                                <div className="w-14 h-14 bg-green-500/20 rounded-full flex items-center justify-center mr-4">
                                    <i className="fas fa-globe-americas text-green-400 text-2xl"></i>
                                </div>
                                <div>
                                    <div className="text-3xl font-bold text-white">14</div>
                                    <div className="text-gray-400">Countries</div>
                                </div>
                            </div>
                            <p className="text-gray-300">Expanding our global footprint</p>
                        </div>
                        <div className="bg-gray-800/50 rounded-xl p-6 backdrop-blur-sm border border-gray-700 hover:border-green-500/50 hover:shadow-[0_0_15px_rgba(16,185,129,0.15)] transition-all duration-300">
                            <div className="flex items-center mb-4">
                                <div className="w-14 h-14 bg-green-500/20 rounded-full flex items-center justify-center mr-4">
                                    <i className="fas fa-city text-green-400 text-2xl"></i>
                                </div>
                                <div>
                                    <div className="text-3xl font-bold text-white">42</div>
                                    <div className="text-gray-400">Cities</div>
                                </div>
                            </div>
                            <p className="text-gray-300">Urban and rural communities served</p>
                        </div>
                        <div className="bg-gray-800/50 rounded-xl p-6 backdrop-blur-sm border border-gray-700 hover:border-green-500/50 hover:shadow-[0_0_15px_rgba(16,185,129,0.15)] transition-all duration-300">
                            <div className="flex items-center mb-4">
                                <div className="w-14 h-14 bg-green-500/20 rounded-full flex items-center justify-center mr-4">
                                    <i className="fas fa-hands-helping text-green-400 text-2xl"></i>
                                </div>
                                <div>
                                    <div className="text-3xl font-bold text-white">120+</div>
                                    <div className="text-gray-400">Partners</div>
                                </div>
                            </div>
                            <p className="text-gray-300">Corporate and community partners</p>
                        </div>
                        <div className="bg-gray-800/50 rounded-xl p-6 backdrop-blur-sm border border-gray-700 hover:border-green-500/50 hover:shadow-[0_0_15px_rgba(16,185,129,0.15)] transition-all duration-300">
                            <div className="flex items-center mb-4">
                                <div className="w-14 h-14 bg-green-500/20 rounded-full flex items-center justify-center mr-4">
                                    <i className="fas fa-users text-green-400 text-2xl"></i>
                                </div>
                                <div>
                                    <div className="text-3xl font-bold text-white">250k+</div>
                                    <div className="text-gray-400">Lives Impacted</div>
                                </div>
                            </div>
                            <p className="text-gray-300">Through education and opportunities</p>
                        </div>
                    </div>
                    
                    <div className="text-center">
                        <p className="text-xl text-gray-300 mb-8">
                            Our network continues to grow, connecting communities and bridging the digital divide.
                        </p>
                        <div className="inline-flex gap-3 items-center">
                            <span className="w-3 h-3 rounded-full bg-green-400"></span>
                            <span className="text-gray-300 mr-6">Base</span>
                            <span className="w-3 h-3 rounded-full bg-gray-400"></span>
                            <span className="text-gray-300">Planned Locations</span>
                        </div>
                    </div>
                </div>
            </section>
        );
    } catch (error) {
        console.error('ImpactMap error:', error);
        if (typeof reportError === 'function') {
            reportError(error);
        }
        
        // Fallback UI when the component fails to render
        return (
            <section className="py-24 bg-gray-900">
                <div className="container mx-auto px-4 text-center">
                    <h2 className="text-4xl font-bold mb-12 text-white">
                        Global <span className="text-green-400">Impact</span>
                    </h2>
                    <div className="bg-gray-800/50 p-8 rounded-xl max-w-lg mx-auto">
                        <i className="fas fa-globe text-gray-600 text-5xl mb-4"></i>
                        <p className="text-white text-xl mb-2">Map temporarily unavailable</p>
                        <p className="text-gray-400">Please check back later to explore our global impact</p>
                    </div>
                </div>
            </section>
        );
    }
}

window.ImpactMap = ImpactMap;

export default ImpactMap;
